import React, { useEffect, useMemo, useRef } from 'react';
import { StyleSheet, View, ViewStyle, StyleProp } from 'react-native';
import { buildRichTextDocument } from './richText/document';
import type { RichTextEditorProps } from './RichTextEditor';
import { useTheme, makeThemedStyles, radii, type V10Palette } from '../v10/theme';

/** Restyles the (unthemed) editor document to the v10 palette by appending
 *  override rules — toolbar on navyRaised, ivory icons, gold active. */
function themeEditorDocument(html: string, t: V10Palette): string {
  const css = `
  html, body { background: ${t.navyRaised}; }
  body { background: linear-gradient(180deg, ${t.navyRaised}, ${t.navy}); }
  #toolbar { background: ${t.navyRaised}; border-bottom: 1px solid ${t.navyLine}; box-shadow: inset 0 1px 0 ${t.panelHighlight}; }
  #toolbar button { background: transparent; border: 1px solid ${t.lineFaint}; color: ${t.ivory}; }
  #toolbar button:active { background: ${t.goldGlow}; }
  #toolbar button.active { background: ${t.gold}; border-color: ${t.goldBright}; color: ${t.onGold}; box-shadow: 0 0 10px ${t.goldGlow}; }
  #toolbar select, #toolbar input[type="number"] { background: ${t.navy}; border: 1px solid ${t.navyLine}; color: ${t.ivory}; }
  #toolbar .sep { background: ${t.navyLine}; }
  .popover { background: ${t.navy}; border: 1px solid ${t.navyLine}; box-shadow: 0 10px 30px ${t.glowSoft}; }
  .popover input[type="text"] { background: ${t.navyRaised}; color: ${t.ivory}; border: 1px solid ${t.navyLine}; }
  .popover .customColorRow label { color: ${t.ivorySoft}; }
  .swatch, .popover input[type="color"] { border-color: ${t.navyLine}; }
  #editor { color: ${t.ivory}; background: transparent; }
  #editor table, #editor th, #editor td { border-color: ${t.navyLine}; }
  #editor th { background: ${t.lineFaint}; }
  #editor blockquote { border-left-color: ${t.gold}; color: ${t.ivorySoft}; }
  #editor pre { background: ${t.navy}; }
  #editor hr { border-top-color: ${t.navyLine}; }
  #editor a { color: ${t.goldBright}; }
  #editor:empty:before { color: ${t.ivoryFaint}; }`;
  return html.replace('</head>', `<style>${css}</style></head>`);
}

/**
 * RichTextEditor (web) — same document as native, hosted in an <iframe
 * srcDoc> because react-native-webview has no web implementation.
 *
 * The iframe is a separate document, so its editor styles can't leak into
 * the app shell and vice versa; messages arrive as plain `postMessage`
 * strings, filtered by the iframe's own contentWindow so no other frame on
 * the page can spoof a change.
 */
export function RichTextEditor({ value, onChange, placeholder, style, pointerEventsNone }: RichTextEditorProps) {
  const { t } = useTheme();
  const styles = useStyles();
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Built once — the iframe document owns the content after mount (same
  // "uncontrolled after mount" contract as the native shell).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const srcDoc = useMemo(() => themeEditorDocument(buildRichTextDocument({ value: value || '', placeholder }), t), []);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;
      if (typeof event.data !== 'string') return;
      try {
        const msg = JSON.parse(event.data);
        if (msg?.source === 'syqi-rich-text' && msg.type === 'change' && typeof msg.html === 'string') {
          onChangeRef.current(msg.html);
        }
      } catch {
        // ignore anything that isn't one of our bridge messages
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  return (
    <View style={[styles.container, style]} pointerEvents={pointerEventsNone ? 'none' : 'auto'}>
      {/* No RN equivalent of an iframe — react-native-web renders DOM elements directly. */}
      <iframe
        ref={frameRef}
        srcDoc={srcDoc}
        title="Rich text editor"
        style={{ width: '100%', height: '100%', border: 'none', display: 'block', background: t.navyRaised }}
      />
    </View>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
  container: {
    // NO `flex: 1` here. In a plain (auto-height) column parent, flex:1 sets
    // flex-basis 0, which overrides the explicit `height` callers pass in and
    // collapses the editor to its toolbar — and made the TKNE Status drag
    // handle look broken, because the height state changed but nothing moved.
    // Callers size this box: either a height in `style`, or `flex: 1` of
    // their own when they want it to fill a flex parent.
    alignSelf: 'stretch',
    minHeight: 200,
    borderRadius: radii.tile,
    overflow: 'hidden',
    backgroundColor: t.navyRaised,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
}),
);

export default RichTextEditor;
